import { reactive } from 'vue';
import { api, ApiClientError } from './api';

/**
 * Web Push through the service worker.
 *
 * The subscription is registered with the existing `register_device_token`
 * endpoint as `device_type: "web"`; the Worker checks for an active row with the
 * same token first, so a device that opens the app fifty times does not end up
 * with fifty tokens (§4).
 */
export const pushState = reactive<{
  status: 'unknown' | 'unsupported' | 'denied' | 'available' | 'subscribed' | 'error';
  message: string;
}>({ status: 'unknown', message: '' });

export async function initPush(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    pushState.status = 'unsupported';
    pushState.message = 'This browser does not support web push.';
    return;
  }
  if (Notification.permission === 'denied') {
    pushState.status = 'denied';
    pushState.message = 'Notifications are blocked in the browser settings.';
    return;
  }
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  pushState.status = existing ? 'subscribed' : 'available';
}

export async function enablePush(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      pushState.status = 'unsupported';
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      pushState.status = 'denied';
      pushState.message = 'Permission was not granted.';
      return;
    }

    const { public_key: publicKey } = await api.get<{ public_key: string }>('/api/push/public-key');
    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToApplicationServerKey(publicKey),
      }));

    await api.post('/api/push/subscribe', {
      subscription: subscription.toJSON(),
      device_model: navigator.userAgent.slice(0, 120),
      app_version: 'web-1.0.0',
    });
    pushState.status = 'subscribed';
    pushState.message = '';
  } catch (error) {
    pushState.status = 'error';
    pushState.message = error instanceof ApiClientError ? error.payload.message : String(error);
  }
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
function urlBase64ToApplicationServerKey(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output.buffer;
}
